import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import RealTerminal from './RealTerminal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function TerminalGame() {
  const { questId } = useParams();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [quest, setQuest] = useState(null);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [levelData, setLevelData] = useState(null);
  const [currentDir, setCurrentDir] = useState('~');
  const [terminalHistory, setTerminalHistory] = useState([]);
  const [commandInput, setCommandInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [containerId, setContainerId] = useState(() => {
    // Recuperar containerId de localStorage al iniciar
    const savedContainerId = localStorage.getItem(`terminal_containerId_${questId}_${user.id}`);
    return savedContainerId || null;
  });
  const [loadingContainer, setLoadingContainer] = useState(false);
  
  const terminalEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchQuest();
  }, [questId]);

  useEffect(() => {
    if (quest && quest.levels) {
      loadLevel(currentLevel);
    }
  }, [quest, currentLevel]);

  useEffect(() => {
    // Auto-scroll to bottom
    terminalEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [terminalHistory]);

  useEffect(() => {
    // Focus input
    inputRef.current?.focus();
  }, []);

  const fetchQuest = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/linux-quest/${questId}`);
      const questData = response.data;
      setQuest(questData);
      
      // Get student progress
      const progressResponse = await axios.get(`${BACKEND_URL}/api/student/${user.id}/linux-quests`);
      const questProgress = progressResponse.data.find(q => q.id === questId);
      
      if (questProgress && questProgress.current_level) {
        setCurrentLevel(questProgress.current_level);
      }

      // If quest uses real terminal, start Docker container
      if (questData.use_real_terminal) {
        const savedContainerId = localStorage.getItem(`terminal_containerId_${questId}_${user.id}`);
        if (savedContainerId) {
          console.log('Container ID found in localStorage, attempting to reconnect...');
        }
        // SIEMPRE llamar a startContainer - el backend decide si reinicia uno existente o crea nuevo
        await startContainer();
      }
    } catch (error) {
      console.error('Error fetching quest:', error);
      alert('Error al cargar Terminal Quest');
    }
  };

  const [hostPort, setHostPort] = useState(() => {
    // Recuperar hostPort de localStorage al iniciar
    const savedHostPort = localStorage.getItem(`terminal_hostPort_${questId}_${user.id}`);
    return savedHostPort ? parseInt(savedHostPort) : null;
  });

  const startContainer = async () => {
    setLoadingContainer(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/linux-quest/${questId}/start-container`,
        { student_id: user.id }
      );
      
      console.log('🔍 Container response:', response.data);
      console.log('🔍 Container ID:', response.data.container_id);
      console.log('🔍 Host Port:', response.data.host_port);
      
      // Actualizar estados
      setContainerId(response.data.container_id);
      setHostPort(response.data.host_port);
      
      // Guardar containerId y hostPort en localStorage para recuperarlos al refrescar
      if (response.data.container_id) {
        localStorage.setItem(`terminal_containerId_${questId}_${user.id}`, response.data.container_id);
        console.log('✅ ContainerId saved to localStorage');
      }
      if (response.data.host_port) {
        localStorage.setItem(`terminal_hostPort_${questId}_${user.id}`, response.data.host_port);
        console.log('✅ HostPort saved to localStorage:', response.data.host_port);
      }
      
      console.log('Container started/restarted successfully');
    } catch (error) {
      console.error('Error starting container:', error);
      alert('Error al iniciar terminal real. Intenta recargar la página.');
    } finally {
      setLoadingContainer(false);
    }
  };

  const handleExitRealTerminal = async (forceDelete = false) => {
    console.log(`🔍 handleExitRealTerminal called with forceDelete: ${forceDelete}`);
    if (containerId) {
      try {
        // Si forceDelete es true, añadir parámetro force
        const url = forceDelete 
          ? `${BACKEND_URL}/api/linux-quest/container/${containerId}?force=true`
          : `${BACKEND_URL}/api/linux-quest/container/${containerId}`;
        console.log(`🔍 Calling DELETE to: ${url}`);
        const response = await axios.delete(url);
        console.log(`🔍 DELETE response:`, response.data);
      } catch (error) {
        console.error('Error stopping container:', error);
      }
      
      // Solo limpiar estado y localStorage si se eliminó el contenedor
      if (forceDelete) {
        setContainerId(null);
        setHostPort(null);
        localStorage.removeItem(`terminal_containerId_${questId}_${user.id}`);
        localStorage.removeItem(`terminal_hostPort_${questId}_${user.id}`);
      } else {
        // Si solo se guardó, mantener el containerId y hostPort en localStorage
        // para poder reconectar después
        console.log('Container saved, keeping in localStorage for reconnection');
      }
    }
    navigate('/dashboard');
  };

  const handleLevelComplete = async (nextLevel) => {
    // Stop current container
    if (containerId) {
      try {
        await axios.delete(`${BACKEND_URL}/api/linux-quest/container/${containerId}`);
      } catch (error) {
        console.error('Error stopping container:', error);
      }
    }
    
    // Update level and restart
    setCurrentLevel(nextLevel);
    setContainerId(null);
    
    // Reload page to start new container with new level
    window.location.reload();
  };

  const loadLevel = (levelNum) => {
    const level = quest.levels.find(l => l.level_number === levelNum);
    if (level) {
      setLevelData(level);
      setCurrentDir('~');
      setTerminalHistory([
        {
          type: 'system',
          content: `=== Nivel ${level.level_number}: ${level.title} ===`
        },
        {
          type: 'system',
          content: level.description
        },
        {
          type: 'system',
          content: ''
        }
      ]);
    }
  };

  const handleCommand = (e) => {
    e.preventDefault();
    if (!commandInput.trim()) return;

    const cmd = commandInput.trim();
    
    // Add to history
    setCommandHistory([...commandHistory, cmd]);
    setHistoryIndex(-1);
    
    // Add command to terminal
    setTerminalHistory(prev => [
      ...prev,
      { type: 'input', content: `student@level${currentLevel}:${currentDir}$ ${cmd}` }
    ]);

    // Process command
    processCommand(cmd);
    setCommandInput('');
  };

  const processCommand = (cmd) => {
    const parts = cmd.split(' ');
    const command = parts[0];
    const args = parts.slice(1);

    switch (command) {
      case 'ls':
        handleLs(args);
        break;
      case 'cat':
        handleCat(args);
        break;
      case 'pwd':
        handlePwd();
        break;
      case 'clear':
        handleClear();
        break;
      case 'grep':
        handleGrep(args);
        break;
      case 'find':
        handleFind(args);
        break;
      case 'hint':
        handleHintCommand();
        break;
      case 'help':
        handleHelp();
        break;
      default:
        addOutput(`bash: ${command}: command not found`);
        addOutput('Comandos disponibles: ls, cat, grep, find, pwd, clear, hint, help');
    }
  };

  const handleLs = (args) => {
    const showHidden = args.includes('-a') || args.includes('-la') || args.includes('-al');
    const files = Object.keys(levelData.filesystem || {});
    
    const visibleFiles = showHidden
      ? files
      : files.filter(f => !f.startsWith('.'));
    
    if (visibleFiles.length === 0) {
      addOutput('');
    } else {
      addOutput(visibleFiles.join('  '));
    }
  };

  const handleCat = (args) => {
    if (args.length === 0) {
      addOutput('cat: falta el operando fichero');
      return;
    }
    
    const filename = args[0];
    const content = levelData.filesystem[filename];
    
    if (content !== undefined) {
      addOutput(content);
    } else {
      addOutput(`cat: ${filename}: No existe el archivo o el directorio`);
    }
  };

  const handlePwd = () => {
    addOutput(`/home/student${currentDir === '~' ? '' : currentDir}`);
  };

  const handleClear = () => {
    setTerminalHistory([]);
  };

  const handleGrep = (args) => {
    if (args.length < 2) {
      addOutput('grep: uso: grep "patrón" archivo');
      return;
    }
    
    const pattern = args[0].replace(/['"]/g, '');
    let files = args.slice(1);
    
    // Si usa -r, buscar en todos los archivos
    if (args[0] === '-r' || args[0] === '-R') {
      const searchPattern = args[1].replace(/['"]/g, '');
      files = Object.keys(levelData.filesystem);
      
      files.forEach(file => {
        const content = levelData.filesystem[file];
        if (content && content.toLowerCase().includes(searchPattern.toLowerCase())) {
          addOutput(`${file}: ${content}`);
        }
      });
    } else {
      files.forEach(file => {
        const content = levelData.filesystem[file];
        if (content && content.toLowerCase().includes(pattern.toLowerCase())) {
          addOutput(content);
        } else if (!levelData.filesystem[file]) {
          addOutput(`grep: ${file}: No existe el archivo o el directorio`);
        }
      });
    }
  };

  const handleFind = (args) => {
    const files = Object.keys(levelData.filesystem || {});
    files.forEach(file => {
      addOutput(`./${file}`);
    });
  };

  const handleHintCommand = () => {
    if (levelData.hint) {
      addOutput(`💡 Pista: ${levelData.hint}`);
      setShowHint(true);
    } else {
      addOutput('No hay pista disponible para este nivel');
    }
  };

  const handleHelp = () => {
    addOutput('Comandos disponibles:');
    addOutput('  ls [-a|-la]  - Lista archivos');
    addOutput('  cat archivo  - Muestra contenido de archivo');
    addOutput('  grep patron archivo - Busca patrón en archivo');
    addOutput('  grep -r patron .    - Busca en todos los archivos');
    addOutput('  find         - Encuentra archivos');
    addOutput('  pwd          - Muestra directorio actual');
    addOutput('  clear        - Limpia terminal');
    addOutput('  hint         - Muestra una pista');
    addOutput('  help         - Muestra esta ayuda');
  };

  const addOutput = (content) => {
    setTerminalHistory(prev => [
      ...prev,
      { type: 'output', content }
    ]);
  };

  const handlePasswordSubmit = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/linux-quest/${questId}/validate-password`, {
        current_level: currentLevel,
        password: passwordInput,
        student_id: user.id
      });

      if (response.data.correct) {
        if (response.data.completed) {
          alert('🎉 ¡Felicitaciones! Has completado el Terminal Quest completo!');
          navigate('/dashboard');
        } else {
          alert(`✅ ${response.data.message}`);
          setCurrentLevel(response.data.next_level);
          setPasswordInput('');
          setShowPasswordModal(false);
          setShowHint(false);
        }
      } else {
        alert(`❌ ${response.data.message}`);
        setPasswordInput('');
      }
    } catch (error) {
      console.error('Error validating password:', error);
      alert('Error al validar el password');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1
          ? commandHistory.length - 1
          : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setCommandInput(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex !== -1) {
        const newIndex = historyIndex + 1;
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1);
          setCommandInput('');
        } else {
          setHistoryIndex(newIndex);
          setCommandInput(commandHistory[newIndex]);
        }
      }
    }
  };

  if (!quest) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Cargando Terminal Quest...</div>
      </div>
    );
  }

  // If quest uses real terminal and container is ready, show RealTerminal component
  if (quest.use_real_terminal) {
    if (loadingContainer || !containerId) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-900">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4"></div>
            <p className="text-white text-xl">Iniciando Terminal Linux Real...</p>
            <p className="text-gray-400 text-sm mt-2">Preparando contenedor Docker</p>
          </div>
        </div>
      );
    }

    // Log current state for debugging
    console.log('🔍 Rendering RealTerminal - ContainerId:', containerId, 'HostPort:', hostPort);

    return (
      <RealTerminal
        containerId={containerId}
        questId={questId}
        questTitle={quest.title}
        levelData={levelData}
        currentLevel={currentLevel}
        totalLevels={quest.levels.length}
        studentId={user.id}
        hostPort={hostPort}
        isPersistent={quest.persistent_containers || false}
        onExit={handleExitRealTerminal}
        onLevelComplete={handleLevelComplete}
      />
    );
  }

  // Simulated terminal (existing code)
  if (!levelData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Cargando nivel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="bg-gray-800 rounded-t-xl p-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">{quest.title}</h1>
            <p className="text-sm text-gray-400">Nivel {currentLevel} de {quest.levels.length}</p>
          </div>
          <div className="flex gap-2">
            {levelData.hint && (
              <button
                onClick={() => setShowHint(!showHint)}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition text-sm"
              >
                💡 {showHint ? 'Ocultar' : 'Ver'} Pista
              </button>
            )}
            <button
              onClick={() => setShowPasswordModal(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm"
            >
              🔑 Ingresar Password
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition text-sm"
            >
              ← Salir
            </button>
          </div>
        </div>

        {/* Hint Panel */}
        {showHint && levelData.hint && (
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
            <p className="text-sm text-yellow-800">
              <strong>💡 Pista:</strong> {levelData.hint}
            </p>
          </div>
        )}

        {/* Terminal */}
        <div className="bg-black rounded-b-xl p-6 font-mono text-sm h-96 overflow-y-auto">
          {terminalHistory.map((entry, index) => (
            <div key={index} className="mb-1">
              {entry.type === 'input' && (
                <div className="text-green-400">{entry.content}</div>
              )}
              {entry.type === 'output' && (
                <div className="text-gray-300">{entry.content}</div>
              )}
              {entry.type === 'system' && (
                <div className="text-cyan-400 font-bold">{entry.content}</div>
              )}
            </div>
          ))}
          
          {/* Command Input */}
          <form onSubmit={handleCommand} className="flex items-center">
            <span className="text-green-400 mr-2">
              student@level{currentLevel}:{currentDir}$
            </span>
            <input
              ref={inputRef}
              type="text"
              value={commandInput}
              onChange={(e) => setCommandInput(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 bg-transparent text-white outline-none"
              autoComplete="off"
              spellCheck="false"
            />
          </form>
          
          <div ref={terminalEndRef} />
        </div>

        {/* Info */}
        <div className="bg-gray-800 rounded-lg p-4 mt-4 text-gray-300 text-sm">
          <p>💡 <strong>Tip:</strong> Usa ↑ ↓ para navegar el historial de comandos. Escribe <code className="bg-gray-900 px-2 py-1 rounded">help</code> para ver comandos disponibles.</p>
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">🔑 Ingresar Password del Nivel {currentLevel + 1}</h3>
            <p className="text-sm text-gray-600 mb-4">
              Si encontraste el password en este nivel, ingrésalo aquí para desbloquear el siguiente.
            </p>
            <input
              type="text"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 font-mono"
              placeholder="nivel2_abc123"
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handlePasswordSubmit}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold"
              >
                Validar
              </button>
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordInput('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TerminalGame;
